import { MouseEvent, ReactNode } from "react";

import { Box, Grid, useTheme } from "@mui/material";

import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { FormButton } from "@/forms-design-system/FormButton";
import { IconElement } from "@/icons/types";

type Tab<T> = {
  label: string;
  value: T;
  icon?: IconElement;
  badge?: ReactNode;
};

interface FormTabMenuProps<T> {
  currentTab?: T;
  onChange: (value: T) => void;
  tabs: Tab<T>[];
  fullWidthButtons?: boolean;
}

export const FormTabMenu = <T,>({ currentTab, onChange, tabs, fullWidthButtons }: FormTabMenuProps<T>) => {
  const { isMediumScreen, isPhoneScreen } = useMediaQuery();
  const theme = useTheme();
  const { selectTextColor, selectBackgroundColor, textColor, backgroundColor } = useDynamicThemeAwareVariables<{
    selectTextColor: string;
    selectBackgroundColor: string;
    textColor: string;
    backgroundColor: string;
  }>(({ mode }) => ({
    textColor: mode === "dark" ? theme.palette.forms.text.supershy : theme.palette.forms.text.moderate,
    backgroundColor: "transparent",
    selectTextColor: theme.palette.forms.text.brand,
    selectBackgroundColor: theme.palette.forms.surface.quiet,
  }));

  const handleClick = (_event: MouseEvent<HTMLButtonElement>, value: T) => {
    onChange(value);
  };

  const displayIcon = isPhoneScreen && tabs.length > 1;

  return (
    <Box
      sx={() => ({
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        padding: 0.5,
        borderRadius: 1,
        boxSizing: "border-box",
      })}
      role="tablist"
      data-test="tab-menu"
    >
      {tabs.map((tab) => {
        const isSelected = currentTab === tab.value;

        if (displayIcon && tab.icon) {
          return (
            <Grid item key={String(tab.value)} data-test="tab-menu-button">
              <FormButton
                onClick={(event) => handleClick(event, tab.value)}
                role="tab"
                aria-selected={isSelected}
                aria-label={tab.label}
                iconButton
                startIcon={tab.icon}
                variant="ghost"
                sx={{
                  backgroundColor: isSelected ? selectBackgroundColor : backgroundColor,
                  color: isSelected ? selectTextColor : textColor,
                  paddingInline: 1.5,
                  gap: 0,
                }}
                fullWidth
              />
            </Grid>
          );
        }

        return (
          <FormButton
            key={String(tab.value)}
            onClick={(event) => handleClick(event, tab.value)}
            role="tab"
            aria-selected={isSelected}
            startIcon={tab.icon}
            data-test="tab-menu-button"
            variant="ghost"
            sx={{
              backgroundColor: isSelected ? selectBackgroundColor : backgroundColor,
              color: isSelected ? selectTextColor : textColor,
              gap: 1,
              flex: isMediumScreen ? 1 : undefined,
              ...(fullWidthButtons && {
                flex: 1,
              }),
            }}
          >
            <span>{tab.label}</span>
            {tab.badge}
          </FormButton>
        );
      })}
    </Box>
  );
};
